import { useState, useRef, useEffect, DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PackageOpen,
  Upload,
  AlertCircle,
  Loader2,
  CheckCircle,
  FileText,
  FileWarning,
  FileX,
  Play,
  Save,
  ArrowLeft,
} from 'lucide-react';
import {
  packageDrop,
  confirmPackageDrop,
  reReviewCase,
  type PackageDropResponse,
} from '../api/cases';
import { PACKAGE_DROP_LABELS } from '../config/labels';
import { useToastStore } from '../stores/toast';
import { useAuthStore } from '../stores/auth';

type Phase = 'idle' | 'uploading' | 'results' | 'confirming' | 'error';

interface AutoField {
  key: string;
  label: string;
  value: string;
  missing: boolean;
}

const FIELD_LABELS: Record<string, string> = {
  issuer_name: 'Issuer Name',
  issuer_symbol: 'Ticker Symbol',
  cusip: 'CUSIP',
  cik: 'CIK',
  depositor_name: 'Depositor Name',
  depositor_type: 'Depositor Type',
  account_ref: 'Account Reference',
  deposit_date: 'Deposit Date',
  share_quantity: 'Share Quantity',
  notes: 'Notes',
};

function docStatusIcon(status: string) {
  if (status === 'ok' || status === 'OK') return <CheckCircle size={16} className="text-green-600" />;
  if (status === 'errored' || status === 'ERRORED') return <FileWarning size={16} className="text-amber-600" />;
  return <FileX size={16} className="text-red-600" />;
}

function docStatusClass(status: string): string {
  if (status === 'ok' || status === 'OK') return 'bg-green-50 text-green-700 border-green-200';
  if (status === 'errored' || status === 'ERRORED') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-red-50 text-red-700 border-red-200';
}

function docStatusLabel(status: string): string {
  if (status === 'ok' || status === 'OK') return PACKAGE_DROP_LABELS.docStatusOk;
  if (status === 'errored' || status === 'ERRORED') return PACKAGE_DROP_LABELS.docStatusErrored;
  return PACKAGE_DROP_LABELS.docStatusNotVerifiable;
}

export default function PackageDrop() {
  const navigate = useNavigate();
  const addToast = useToastStore((s) => s.addToast);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<Phase>('idle');
  const [dragOver, setDragOver] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [caseId, setCaseId] = useState<string | null>(null);
  const [dropResult, setDropResult] = useState<PackageDropResponse | null>(null);
  const [autoFields, setAutoFields] = useState<AutoField[]>([]);
  const [pullData, setPullData] = useState(true);
  const [processWithoutAI, setProcessWithoutAI] = useState(false);
  // DIR-043: admin-only per-drop override of the ingest PII stripping mode.
  // '' = follow the config default (ingest_stripping_mode).
  const [ingestStrippingOverride, setIngestStrippingOverride] = useState<string>('');
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'operator' || user?.role === 'principal';
  // Profile (dev/uat/prod) from /health. The "Process documents without AI"
  // checkbox is hidden outside dev (it doesn't produce useful auto-fill yet --
  // the deterministic/regex extractor is too loose; accurate extraction needs
  // the LLM). Operator direction 2026-10-01: hide in RC.
  const [envProfile, setEnvProfile] = useState<string>('');
  useEffect(() => {
    const base = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL || '/api/v1';
    fetch(`${base}/health`).then(r => r.json()).then((d: { profile?: string }) => { if (d.profile) setEnvProfile(d.profile); }).catch(() => {});
  }, []);
  const showNoAiOption = envProfile === 'dev';

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;
    await processFiles(droppedFiles);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    await processFiles(selected);
  };

  const processFiles = async (selectedFiles: File[]) => {
    setFiles(selectedFiles);
    setError(null);
    setPhase('uploading');

    try {
      // Call the proper package-drop endpoint (handles upload + extract + auto-fill)
      const result = await packageDrop(selectedFiles, processWithoutAI, ingestStrippingOverride || null);
      setDropResult(result);
      setCaseId(result.case_id);

      // Build auto-fill fields from the response
      const fields: AutoField[] = [];
      const fieldMap: Record<string, string | null> = {
        issuer_name: result.issuer_name,
        issuer_symbol: result.issuer_symbol,
        cusip: result.cusip,
        cik: result.cik,
        depositor_name: result.depositor_name,
        depositor_type: result.depositor_type,
        account_ref: result.account_ref,
        deposit_date: result.deposit_date,
        share_quantity: result.share_quantity ? String(result.share_quantity) : null,
        notes: result.notes,
      };

      for (const [key, val] of Object.entries(fieldMap)) {
        const isMissing = result.missing_fields.includes(key);
        fields.push({
          key,
          label: FIELD_LABELS[key] || key,
          value: val || '',
          missing: isMissing,
        });
      }
      setAutoFields(fields);
      setPhase('results');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to process files'
      );
      setPhase('error');
    }
  };

  const resetAll = () => {
    setPhase('idle');
    setFiles([]);
    setError(null);
    setCaseId(null);
    setDropResult(null);
    setAutoFields([]);
  };

  const handleFieldChange = (idx: number, value: string) => {
    setAutoFields((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, value } : f))
    );
  };

  const handleConfirm = async (mode: 'save' | 'review') => {
    if (!caseId) return;
    setPhase('confirming');
    try {
      // Build updates from edited fields
      const updates: Record<string, unknown> = {};
      for (const f of autoFields) {
        if (f.value.trim()) {
          updates[f.key] = f.value.trim();
        }
      }

      if (mode === 'review' && pullData) {
        // Save the case (no review yet), then pull public data + launch the
        // review via the re-review endpoint (EDGAR/OTC/FINRA/OFAC/etc.).
        await confirmPackageDrop(caseId, updates, false);
        await reReviewCase(caseId);
        addToast('Pulling public data + running review...', 'success');
      } else {
        const runReviewFlag = mode === 'review';
        await confirmPackageDrop(caseId, updates, runReviewFlag);
        addToast(runReviewFlag ? PACKAGE_DROP_LABELS.reviewStarted : PACKAGE_DROP_LABELS.caseSaved, 'success');
      }
      navigate(`/cases/${caseId}`);
    } catch {
      addToast('Failed to save case', 'error');
      setPhase('results');
    }
  };

  const hasMissingFields = autoFields.some((f) => f.missing);
  const documents = dropResult?.documents || [];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <PackageOpen className="text-primary-600" size={24} />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {PACKAGE_DROP_LABELS.title}
          </h1>
          <p className="text-sm text-slate-500">
            {PACKAGE_DROP_LABELS.subtitle}
          </p>
        </div>
      </div>

      {/* IDLE: Drop Zone */}
      {phase === 'idle' && (
        <div className="card p-8 animate-fade-in">
          <div
            className={`dropzone ${dragOver ? 'dropzone-active' : ''} py-16`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            aria-label="Upload package files"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <Upload className="mx-auto text-slate-400 mb-3" size={40} />
            <p className="text-base font-medium text-slate-700">
              {PACKAGE_DROP_LABELS.dropZone}
            </p>
            <p className="text-sm text-slate-400 mt-1">
              {PACKAGE_DROP_LABELS.dropZoneHint}
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          {isAdmin && showNoAiOption && (
            <>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none mt-4 justify-center">
                <input
                  type="checkbox"
                  checked={processWithoutAI}
                  onChange={(e) => setProcessWithoutAI(e.target.checked)}
                  disabled={(phase as Phase) === 'uploading' || (phase as Phase) === 'confirming'}
                  className="mt-0"
                />
                <span>Process documents without AI (skip AI analysis on upload)</span>
              </label>
              <div className="flex flex-col items-center gap-1 mt-3">
                <label htmlFor="ingest-stripping-override" className="text-xs text-slate-500">
                  Ingest stripping (override)
                </label>
                <select
                  id="ingest-stripping-override"
                  value={ingestStrippingOverride}
                  onChange={(e) => setIngestStrippingOverride(e.target.value)}
                  disabled={(phase as Phase) === 'uploading' || (phase as Phase) === 'confirming'}
                  className="text-sm border border-slate-300 rounded-md px-2 py-1 disabled:opacity-50"
                >
                  <option value="">Default (config)</option>
                  <option value="regex">Pattern redaction</option>
                  <option value="ai_local">Local AI rewrite</option>
                  <option value="none">No stripping</option>
                </select>
              </div>
            </>
          )}
          <p className="text-center text-xs text-slate-400 mt-4">
            {PACKAGE_DROP_LABELS.noFiles}
          </p>
        </div>
      )}

      {/* UPLOADING: Progress */}
      {phase === 'uploading' && (
        <div className="card p-8 animate-fade-in">
          <div className="flex flex-col items-center text-center">
            <Loader2 className="animate-spin text-primary-600 mb-4" size={40} />
            <p className="text-base font-medium text-slate-700">
              {PACKAGE_DROP_LABELS.uploadProgress.replace(
                '{n}',
                String(files.length)
              )}
            </p>
            <p className="text-sm text-slate-400 mt-2">
              Extracting text, analyzing documents, and auto-filling case fields...
            </p>
            <ul className="mt-4 space-y-1 text-left w-full max-w-md">
              {files.map((file, idx) => (
                <li
                  key={idx}
                  className="flex items-center gap-2 text-sm text-slate-600"
                >
                  <FileText size={14} className="text-slate-400 shrink-0" />
                  <span className="truncate">{file.name}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ERROR */}
      {phase === 'error' && (
        <div className="card p-8 animate-fade-in">
          <div className="flex flex-col items-center text-center">
            <AlertCircle className="text-red-500 mb-4" size={40} />
            <p className="text-base font-medium text-red-700">
              {PACKAGE_DROP_LABELS.intakeError}
            </p>
            <p className="text-sm text-slate-500 mt-1">{error}</p>
            <button onClick={resetAll} className="btn-primary mt-4">
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* RESULTS: Auto-filled fields + documents */}
      {(phase === 'results' || phase === 'confirming') && (
        <div className="space-y-4 animate-fade-in">
          {/* Success banner */}
          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm border border-green-200">
            <CheckCircle size={18} />
            <span>{PACKAGE_DROP_LABELS.intakeComplete}</span>
            {dropResult?.case_number && (
              <span className="ml-2 font-medium">
                Case: {dropResult.case_number}
              </span>
            )}
          </div>

          {/* Missing fields warning */}
          {hasMissingFields && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-700 text-sm border border-amber-200">
              <AlertCircle size={18} className="shrink-0 mt-0.5" />
              <span>{PACKAGE_DROP_LABELS.manualReviewNeeded}</span>
            </div>
          )}

          {/* Auto-filled fields (editable) */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-1">
              {PACKAGE_DROP_LABELS.autoFilled}
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              {PACKAGE_DROP_LABELS.editFields}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {autoFields.map((field, idx) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {field.label}
                    {field.missing && (
                      <span className="ml-2 text-xs text-amber-600 font-normal">
                        (missing - fill in)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    className={`input ${
                      field.missing
                        ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-500'
                        : ''
                    }`}
                    value={field.value}
                    onChange={(e) => handleFieldChange(idx, e.target.value)}
                    placeholder={field.missing ? 'Enter manually...' : 'Auto-filled'}
                    aria-label={field.label}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Documents with status */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">
              {PACKAGE_DROP_LABELS.documents}
            </h2>
            {documents.length === 0 ? (
              <p className="text-sm text-slate-400">
                No documents were processed.
              </p>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.document_id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50"
                  >
                    {docStatusIcon(doc.processing_status)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">
                        {doc.original_filename}
                      </p>
                      {doc.classification && (
                        <p className="text-xs text-slate-400">
                          {doc.classification}
                        </p>
                      )}
                      {doc.processing_error && (
                        <p className="text-xs text-red-500">
                          {doc.processing_error}
                        </p>
                      )}
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded border ${docStatusClass(
                        doc.processing_status
                      )}`}
                    >
                      {docStatusLabel(doc.processing_status)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row justify-between gap-3">
            <button
              onClick={resetAll}
              className="btn-secondary"
              disabled={phase === 'confirming'}
            >
              <ArrowLeft size={18} />
              Upload Different Files
            </button>
            <div className="flex gap-3 flex-wrap items-center">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none mr-1">
                <input
                  type="checkbox"
                  checked={pullData}
                  onChange={(e) => setPullData(e.target.checked)}
                  disabled={phase === 'confirming'}
                  className="mt-0"
                />
                <span>Pull public data before review</span>
              </label>
              <button
                onClick={() => handleConfirm('save')}
                disabled={phase === 'confirming'}
                className="btn-secondary"
              >
                {phase === 'confirming' ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Save size={18} />
                )}
                {PACKAGE_DROP_LABELS.saveOnly}
              </button>
              <button
                onClick={() => handleConfirm('review')}
                disabled={phase === 'confirming'}
                className="btn-primary"
              >
                {phase === 'confirming' ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Play size={18} />
                )}
                {PACKAGE_DROP_LABELS.confirmAndRun}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}